/*
  AMMO Shop All, version A (Oct 28, 2026, Claude): every product is already in the page, so filtering, search, counts
  and sorting all happen here with no reloads.
  - Filters: Shop by (category > type, a tree), Surface and Price. OR inside a group, AND across groups.
  - Each option's number is how many products you'd see if you ticked it, given the search and the other groups.
  - Search narrows as you type: every word must start a word in the product's title, tagline, tags or categories.
  - Featured uses pinned picks then sales rank; other sorts put sold-out products at the end. The state lives in the URL, so Back from a product page restores it.
  Categories come from snippets/ammo-shop-all-catalog.liquid; a product missing there falls back to its collections.
*/
(() => {
  const root = document.querySelector('[data-asa]');
  if (!root) return;

  // Old links sort on the server (?sort_by=…); the page needs best-selling order underneath, so swap to our own param.
  // Old ?page=2 links from the 24-a-page grid would open an empty page: every product is on page 1 now
  const SORT0 = root.dataset.asaDefaultSort || 'featured';
  const LEGACY = { manual: 'featured', 'best-selling': 'best', 'price-ascending': 'price-asc', 'price-descending': 'price-desc', 'created-descending': 'new', 'created-ascending': 'og' };
  const start = new URL(location.href);
  const oldPage = start.searchParams.has('page') && !root.querySelector('[data-asa-item]');
  if (start.searchParams.has('sort_by') || oldPage) {
    const mapped = LEGACY[start.searchParams.get('sort_by')];
    start.searchParams.delete('sort_by');
    start.searchParams.delete('page');
    if (mapped && mapped !== SORT0) start.searchParams.set('sort', mapped);
    location.replace(start);
    return;
  }

  const cfg = JSON.parse(root.querySelector('[data-asa-catalog]').textContent);
  const $ = (s, el = root) => el.querySelector(s);
  const $$ = (s, el = root) => [...el.querySelectorAll(s)];
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const stem = t => (t.length > 4 && t.endsWith('es') ? t.slice(0, -2) : t.length > 3 && t.endsWith('s') ? t.slice(0, -1) : t);
  const plural = n => (n === 1 ? '1 product' : `${n} products`);

  const CATS = cfg.cats.map(([id, label, subs = []]) => ({ id, label, subs: subs.map(([sid, sl]) => ({ id: `${id}-${sid}`, label: sl, parent: id })) }));
  const SURF = cfg.surfaces.map(([code, id, label]) => ({ code, id, label }));
  const PRICE = cfg.prices.map(([id, min, max, label]) => ({ id, min, max, label }));
  const LABEL = {};
  CATS.forEach(c => { LABEL[c.id] = c.label; c.subs.forEach(s => (LABEL[s.id] = s.label)); });
  SURF.forEach(s => (LABEL[s.code] = s.label));
  const inBucket = (it, p) => it.price >= p.min && (p.max == null || it.price < p.max);

  // A product the catalogue doesn't know yet: place it from its collections
  const fallback = cols => {
    const has = h => cols.includes(h);
    const c = has('kits') ? 'kits/' : has('gift-cards') ? 'gear/gift' : has('apparel-decals') && !has('tools') ? 'gear/apparel'
      : has('paint-ceramic') || has('wheel-ceramic') ? 'coatings/ceramic' : has('formulas') ? 'formulas/' : has('protection') ? 'coatings/'
      : has('tools') ? 'tools/' : 'other/';
    return `${c}/${has('exterior') ? 'P' : ''}${has('wheels') ? 'W' : ''}${has('interior') ? 'I' : ''}`;
  };

  // Shopify tags mix broad ones ("interior", "tools", "bestseller") that would disagree with the filters with specific
  // ones ("battery tender", "clear bra"). Search keeps the tags that sit on three products or fewer
  const tagsOf = el => (el.dataset.g || '').split('|').map(t => t.trim().toLowerCase()).filter(Boolean);
  const tagCount = {};
  $$('[data-asa-item]').forEach(el => tagsOf(el).forEach(t => (tagCount[t] = (tagCount[t] || 0) + 1)));

  const grid = $('[data-asa-grid]');
  const items = $$('[data-asa-item]').map((el, i) => {
    const h = el.dataset.h;
    const [spec, extra = ''] = (cfg.p[h] || fallback((el.dataset.c || '').split(' '))).split('|');
    const [cat, sub, surf = ''] = spec.split('/');
    const it = {
      el, h, cat, sub: sub ? `${cat}-${sub}` : '', surfs: new Set(surf.split('').filter(Boolean)),
      price: +el.dataset.p || 0, date: +el.dataset.d || 0, avail: el.dataset.a === '1',
      best: i,
    };
    const tags = tagsOf(el).filter(t => tagCount[t] <= 3);
    const text = [el.dataset.t, extra, ...tags, LABEL[cat], LABEL[it.sub], ...[...it.surfs].map(c => LABEL[c])].filter(Boolean).join(' ');
    it.words = norm(text).split(/[^a-z0-9]+/).filter(Boolean);
    return it;
  });
  const TOTAL = items.length;

  // Featured: the configured top picks, then best-selling order. Ultimate's per-view
  // first/last placement is applied in applySort. Price does not influence this order.
  const featuredPicks = Object.values(cfg.featured || {}).flat();
  items.forEach(it => {
    const rank = featuredPicks.indexOf(it.h);
    it.feat = rank < 0 ? featuredPicks.length : rank;
  });

  const state = { q: '', type: new Set(), surface: new Set(), price: new Set(), sort: SORT0 };
  const SORTS = {
    featured: (a, b) => a.feat - b.feat || a.best - b.best,
    best: (a, b) => a.best - b.best,
    'price-asc': (a, b) => a.price - b.price || a.best - b.best,
    'price-desc': (a, b) => b.price - a.price || a.best - b.best,
    new: (a, b) => b.date - a.date || a.best - b.best,
    og: (a, b) => a.date - b.date || a.best - b.best,
  };
  // What the Sort button says on phones and tablets (desktop spells out "Sort by: …"), so the current sort is visible
  const SORT_SHORT = { featured: 'Featured', best: 'Best selling', new: 'Newest', og: 'OGs first', 'price-asc': 'Low price', 'price-desc': 'High price' };

  // ---------- matching and counts ----------
  const tokens = () => norm(state.q).split(/[^a-z0-9]+/).filter(Boolean).map(stem);
  const mQ = (it, toks) => toks.every(t => it.words.some(w => w.startsWith(t)));
  // Gary, Nov 3: the complete kit is also an end-of-list option in the broad Featured product categories.
  // Keep subtype, search, surface and price filters meaningful; it is never a Gear product.
  const isUltimate = it => it.h === 'ultimate-ammo-kit';
  const inType = (it, id) => it.cat === id || it.sub === id ||
    (state.sort === 'featured' && isUltimate(it) && ['formulas', 'coatings', 'tools'].includes(id));
  const mType = it => !state.type.size || [...state.type].some(id => inType(it, id));
  const mSurf = it => !state.surface.size || [...state.surface].some(c => it.surfs.has(c));
  const mPrice = it => !state.price.size || PRICE.some(p => state.price.has(p.id) && inBucket(it, p));

  function compute() {
    const toks = tokens();
    const rows = items.map(it => ({ it, q: mQ(it, toks), t: mType(it), s: mSurf(it), p: mPrice(it) }));
    const n = {};
    const count = (key, test) => (n[key] = rows.reduce((k, r) => k + (test(r) ? 1 : 0), 0));
    CATS.forEach(c => {
      count(c.id, r => r.q && r.s && r.p && inType(r.it, c.id));
      c.subs.forEach(s => count(s.id, r => r.q && r.s && r.p && r.it.sub === s.id));
    });
    SURF.forEach(s => count(`s:${s.code}`, r => r.q && r.t && r.p && r.it.surfs.has(s.code)));
    PRICE.forEach(p => count(`p:${p.id}`, r => r.q && r.t && r.s && inBucket(r.it, p)));
    // A card replaces the category and surface choices, so its number keeps only the search and price
    CARDS.forEach((c, i) => count(`c:${i}`, r => r.q && r.p && cardMatch(c, r.it)));
    const shown = rows.filter(r => r.q && r.t && r.s && r.p).map(r => r.it);
    return { shown: new Set(shown), n, total: shown.length };
  }

  // Ticking a whole category replaces its types; ticking every type collapses back to the category
  function toggleType(id, on) {
    const cat = CATS.find(c => c.id === id || c.subs.some(s => s.id === id));
    if (!cat) return;
    if (id === cat.id) {
      cat.subs.forEach(s => state.type.delete(s.id));
      on ? state.type.add(id) : state.type.delete(id);
      return;
    }
    if (state.type.has(cat.id)) {
      state.type.delete(cat.id);
      cat.subs.forEach(s => s.id !== id && state.type.add(s.id));
    }
    on ? state.type.add(id) : state.type.delete(id);
    if (cat.subs.every(s => state.type.has(s.id))) {
      cat.subs.forEach(s => state.type.delete(s.id));
      state.type.add(cat.id);
    }
  }

  // ---------- URL ----------
  const OURS = ['q', 'type', 'surface', 'price', 'sort', 'page'];
  function readURL() {
    const u = new URL(location.href);
    state.q = u.searchParams.get('q') || '';
    const list = k => (u.searchParams.get(k) || '').split(',').filter(Boolean);
    list('type').forEach(id => LABEL[id] && CATS.some(c => c.id === id || c.subs.some(s => s.id === id)) && toggleType(id, true));
    list('surface').forEach(id => { const s = SURF.find(x => x.id === id); s && state.surface.add(s.code); });
    list('price').forEach(id => PRICE.some(p => p.id === id) && state.price.add(id));
    const sort = u.searchParams.get('sort');
    if (SORTS[sort]) state.sort = sort;
  }
  // Written a moment after the last change, and at once when the shopper leaves (a card tap, Back), so Back restores it
  let urlTimer = 0;
  function writeURL() {
    clearTimeout(urlTimer);
    urlTimer = setTimeout(flushURL, 250);
  }
  function flushURL() {
    if (!urlTimer) return;
    clearTimeout(urlTimer);
    urlTimer = 0;
    const u = new URL(location.href);
    OURS.forEach(k => u.searchParams.delete(k));
    if (state.q.trim()) u.searchParams.set('q', state.q.trim());
    if (state.type.size) u.searchParams.set('type', [...state.type].join(','));
    if (state.surface.size) u.searchParams.set('surface', SURF.filter(s => state.surface.has(s.code)).map(s => s.id).join(','));
    if (state.price.size) u.searchParams.set('price', PRICE.filter(p => state.price.has(p.id)).map(p => p.id).join(','));
    if (state.sort !== SORT0) u.searchParams.set('sort', state.sort);
    history.replaceState(history.state, '', u.toString().replace(/%2C/g, ','));
  }
  addEventListener('pagehide', flushURL);
  root.addEventListener('click', e => e.target.closest('a[href]') && flushURL(), true);

  // ---------- filter panel ----------
  const chev = '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const opt = (kind, value, label, key) => `
    <label class="asa-opt">
      <input type="checkbox" data-asa-${kind}="${esc(value)}">
      <span class="asa-box" aria-hidden="true"></span>
      <span class="asa-opt__label">${esc(label)}</span>
      <span class="asa-n" data-asa-n="${esc(key)}"></span>
    </label>`;
  $('[data-asa-groups]').innerHTML = `
    <section class="asa-group" aria-labelledby="AsaGroupType">
      <h2 class="asa-group__title" id="AsaGroupType">Shop by</h2>
      <ul class="asa-tree" role="list">
        ${CATS.map(c => `
          <li class="asa-node" data-asa-node="${c.id}">
            <div class="asa-node__row">
              ${opt('type', c.id, c.label, c.id)}
              ${c.subs.length ? `<button type="button" class="asa-twist" aria-expanded="false" aria-controls="AsaSub-${c.id}" aria-label="${esc(c.label)} types">${chev}</button>` : '<span class="asa-gap" aria-hidden="true"></span>'}
            </div>
            ${c.subs.length ? `<ul class="asa-sub" id="AsaSub-${c.id}" role="list" hidden>${c.subs.map(s => `<li>${opt('type', s.id, s.label, s.id)}</li>`).join('')}</ul>` : ''}
          </li>`).join('')}
      </ul>
    </section>
    <section class="asa-group" aria-labelledby="AsaGroupSurface">
      <h2 class="asa-group__title" id="AsaGroupSurface">Surface</h2>
      <ul class="asa-list" role="list">${SURF.map(s => `<li>${opt('surface', s.code, s.label, `s:${s.code}`)}</li>`).join('')}</ul>
    </section>
    <section class="asa-group" aria-labelledby="AsaGroupPrice">
      <h2 class="asa-group__title" id="AsaGroupPrice">Price</h2>
      <ul class="asa-list" role="list">${PRICE.map(p => `<li>${opt('price', p.id, p.label, `p:${p.id}`)}</li>`).join('')}</ul>
    </section>`;

  const qInputs = $$('[data-asa-q]');
  const sorts = $$('[data-asa-sort]');
  const counts = $$('[data-asa-n]');
  const typeBoxes = $$('[data-asa-type]');
  const surfBoxes = $$('[data-asa-surface]');
  const priceBoxes = $$('[data-asa-price]');
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- top cards ----------
  // The collection's photo cards, the same menu other collection pages show. A card the catalogue maps filters in place
  // and shows its view: tapping it replaces the category and surface choices, tapping it again clears it. The Filter
  // panel stays the place to combine. A card the catalogue doesn't know stays an ordinary link
  const CARDS = $$('[data-asa-card]').map(el => {
    const [group, value] = (cfg.cards || {})[el.dataset.asaCard] || [];
    if (!group) return null;
    el.setAttribute('role', 'button');
    return { el, group, value, n: $('[data-asa-card-n]', el), label: $('.asa-card__name', el).textContent.trim() };
  }).filter(Boolean);
  // A category card stays lit while any of its types is chosen (Tools while narrowed to Towels); tapping it clears them
  const cardOn = c => (c.group === 'type'
    ? [...state.type].some(id => id === c.value || id.startsWith(`${c.value}-`))
    : state.surface.has(c.value));
  const cardMatch = (c, it) => (c.group === 'type' ? inType(it, c.value) : it.surfs.has(c.value));
  const titleEl = $('.asa-title');
  const TITLE = titleEl.textContent;
  function tapCard(c) {
    if (cardOn(c)) {
      c.group === 'type' ? toggleType(c.value, false) : state.surface.delete(c.value);
    } else {
      state.type.clear();
      state.surface.clear();
      c.group === 'type' ? toggleType(c.value, true) : state.surface.add(c.value);
      if (c.group === 'type') expand($(`[data-asa-node="${c.value}"]`), true);
    }
    update();
  }

  // Kits has no types, so it has no list to open
  function expand(node, open) {
    const tw = node && $('.asa-twist', node);
    const sub = node && $('.asa-sub', node);
    if (!tw || !sub) return;
    tw.setAttribute('aria-expanded', String(open));
    sub.hidden = !open;
  }

  function applySort() {
    const ultimateFirst = !state.surface.size && (!state.type.size ||
      (state.type.size === 1 && state.type.has('kits')));
    const sorted = [...items].sort((a, b) => {
      if (state.sort === 'featured' && isUltimate(a) !== isUltimate(b)) {
        return (isUltimate(a) ? -1 : 1) * (ultimateFirst ? 1 : -1);
      }
      if (state.sort === 'featured') return SORTS.featured(a, b);
      return (b.avail - a.avail) || SORTS[state.sort](a, b);
    });
    grid.append(...sorted.map(it => it.el));
  }

  function chips() {
    const list = [];
    if (state.q.trim()) list.push(['q', '', `“${state.q.trim()}”`]);
    state.type.forEach(id => list.push(['type', id, LABEL[id]]));
    SURF.forEach(s => state.surface.has(s.code) && list.push(['surface', s.code, s.label]));
    PRICE.forEach(p => state.price.has(p.id) && list.push(['price', p.id, p.label]));
    $('[data-asa-chips]').innerHTML = list.map(([k, v, l]) =>
      `<button type="button" class="asa-chip" data-asa-chip="${k}" data-v="${esc(v)}" aria-label="Remove ${esc(l)}">${esc(l)}<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="m3 3 6 6M9 3 3 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg></button>`
    ).join('') + (list.length > 1 ? '<button type="button" class="asa-chip asa-chip--clear" data-asa-clear>Clear all</button>' : '');
    return list.length;
  }

  // ---------- refine row ----------
  // One step down from what's chosen: a category's types (Tools: Towels, Brushes…) or, for a surface on its own, the
  // categories inside it (Paint: Kits, Formulas…). Dan watched a shopper open Tools, see a steamer and leave; the row
  // shows the range at a glance. One tap picks one; the Filter panel stays the place to combine
  const refineEl = $('[data-asa-refine]');
  function refineView() {
    const touched = CATS.filter(c => state.type.has(c.id) || c.subs.some(s => state.type.has(s.id)));
    if (touched.length === 1 && touched[0].subs.length) return { cat: touched[0] };
    if (!touched.length && state.surface.size === 1) return { cats: CATS };
    return null;
  }
  function refine(n) {
    const view = refineView();
    refineEl.hidden = !view;
    if (!view) return void (refineEl.innerHTML = '');
    const pill = (id, label, on) => `<button type="button" class="asa-ref" data-asa-ref="${esc(id)}" aria-pressed="${on}"${!on && !n[id] ? ' disabled' : ''}>${esc(label)}<span class="asa-ref__n">${n[id] || 0}</span></button>`;
    let html;
    if (view.cat) {
      const c = view.cat;
      const whole = state.type.has(c.id);
      html = pill(c.id, `All ${c.label.toLowerCase()}`, whole) + c.subs.map(s => pill(s.id, s.label, !whole && state.type.has(s.id))).join('');
    } else {
      html = view.cats.filter(c => n[c.id]).map(c => pill(c.id, c.label, false)).join('');
    }
    refineEl.innerHTML = `<div class="asa-refine__row" role="group" aria-label="Narrow down">${html}</div>`;
  }
  // A category (or "All …") takes the whole category; a type becomes the only one, and tapping it again goes back to all
  function tapRefine(id) {
    const cat = CATS.find(c => c.id === id || c.subs.some(s => s.id === id));
    if (!cat) return;
    const ids = [cat.id, ...cat.subs.map(s => s.id)];
    const only = id !== cat.id && state.type.has(id) && ids.filter(x => state.type.has(x)).length === 1;
    ids.forEach(x => state.type.delete(x));
    state.type.add(only ? cat.id : id);
    expand($(`[data-asa-node="${cat.id}"]`), true);
    update();
  }

  // After a change the results start just under the sticky bar. Otherwise a shorter list leaves a shopper who had
  // scrolled down looking at the footer. With the filter panel open this happens behind it
  function toResults() {
    // Where the bar sits once stuck; measuring it now fails when a short list has carried it off screen
    const header = parseFloat(getComputedStyle(root).getPropertyValue('--asa-top')) || 0;
    const under = header + $('.asa-bar').offsetHeight;
    const top = $('.asa-main').getBoundingClientRect().top;
    if (top < under - 1) window.scrollBy(0, top - under);
  }

  // The page is titled for the view it's on, maybe narrowed (Tools from the menu opens on Towels): a card's name, or for
  // the views with no card (Coatings, Formulas, Glass) the category or surface itself. Larry, Oct 29: Tools lit up, but
  // on Coatings nothing showed where he was
  function viewOf() {
    const card = CARDS.find(c => c.group === 'type'
      ? !state.surface.size && state.type.size && [...state.type].every(id => id === c.value || id.startsWith(`${c.value}-`))
      : state.surface.size === 1 && state.surface.has(c.value));
    if (card) return { label: card.label, size: items.filter(it => cardMatch(card, it)).length };
    const cats = CATS.filter(c => [...state.type].some(id => id === c.id || id.startsWith(`${c.id}-`)));
    if (cats.length === 1 && !state.surface.size) return { label: cats[0].label, size: items.filter(it => inType(it, cats[0].id)).length };
    const surf = SURF.find(s => state.surface.has(s.code));
    if (state.surface.size === 1) return { label: surf.label, size: items.filter(it => it.surfs.has(surf.code)).length };
    return null;
  }

  // Best Sellers browses the full catalogue in batches while retaining all products for instant filters/sorts.
  const more = $('[data-asa-more]');
  const batchSize = 24;
  let visibleLimit = more ? Math.max(batchSize, Number(history.state?.asaVisible) || batchSize) : batchSize;
  let firstPaint = true;
  function update(resetWindow = true) {
    if (resetWindow) visibleLimit = batchSize;
    applySort();
    const { shown, n, total } = compute();
    const matched = new Set([...shown].map(it => it.el));
    let visibleCount = 0;
    for (const el of grid.children) {
      el.hidden = !matched.has(el) || (!!more && visibleCount >= visibleLimit);
      if (!el.hidden) visibleCount++;
    }
    if (more) {
      more.hidden = visibleCount >= total;
      $('[data-asa-progress]', more).textContent = `${visibleCount} of ${plural(total)}`;
      history.replaceState({ ...history.state, asaVisible: visibleLimit }, '', location.href);
    }
    // The first cards on screen after a filter or sort may come from far down the page: fetch their photos now
    let first = 0;
    for (const el of grid.children) {
      if (el.hidden) continue;
      const img = el.querySelector('img[loading="lazy"]');
      if (img) img.loading = 'eager';
      if (++first === 6) break;
    }

    counts.forEach(el => {
      const v = String(n[el.dataset.asaN] ?? 0);
      if (el.textContent !== v) {
        el.textContent = v;
        if (!firstPaint && !reduceMotion) { el.classList.remove('asa-n--tick'); void el.offsetWidth; el.classList.add('asa-n--tick'); }
      }
    });
    typeBoxes.forEach(box => {
      const id = box.dataset.asaType;
      const cat = CATS.find(c => c.id === id);
      const on = state.type.has(id) || (!cat && state.type.has(id.split('-')[0]));
      box.checked = on;
      box.indeterminate = !!cat && !on && cat.subs.some(s => state.type.has(s.id));
      box.disabled = !on && !box.indeterminate && !n[id];
    });
    surfBoxes.forEach(box => { box.checked = state.surface.has(box.dataset.asaSurface); box.disabled = !box.checked && !n[`s:${box.dataset.asaSurface}`]; });
    priceBoxes.forEach(box => { box.checked = state.price.has(box.dataset.asaPrice); box.disabled = !box.checked && !n[`p:${box.dataset.asaPrice}`]; });

    const active = state.type.size + state.surface.size + state.price.size;
    const nChips = chips();
    refine(n);
    root.classList.toggle('asa--filtered', nChips > 0);
    const view = viewOf();
    const base = view ? view.size : TOTAL;
    titleEl.textContent = view ? view.label : TITLE;
    $('[data-asa-count]').textContent = total === base ? plural(base) : `${total} of ${plural(base)}`;
    const badge = $('[data-asa-nactive]');
    badge.hidden = !active;
    badge.textContent = active || '';
    const sortPill = $('.asa-pill--sort');
    sortPill.classList.toggle('asa-pill--on', state.sort !== SORT0);
    $('[data-asa-sort-label]', sortPill).textContent = SORT_SHORT[state.sort];
    $('[data-asa-sort-long]', sortPill).textContent = $('select', sortPill).selectedOptions[0]?.text || '';
    root.classList.toggle('asa--card-on', CARDS.some(cardOn));
    CARDS.forEach((c, i) => {
      const on = cardOn(c);
      const none = !on && !n[`c:${i}`];
      c.el.setAttribute('aria-pressed', String(on));
      c.el.setAttribute('aria-disabled', String(none));
      c.n.textContent = n[`c:${i}`];
    });
    $('[data-asa-show]').textContent = total ? `Show ${plural(total)}` : 'No matches yet';

    const empty = $('[data-asa-empty]');
    empty.hidden = total > 0;
    $('[data-asa-empty-q]').textContent = state.q.trim() ? ` for “${state.q.trim()}”` : '';
    const all = $('[data-asa-search-all]');
    all.href = `${all.pathname}${state.q.trim() ? `?q=${encodeURIComponent(state.q.trim())}` : ''}`;

    if (!firstPaint && resetWindow) toResults();
    firstPaint = false;
    writeURL();
  }

  // ---------- events ----------
  qInputs.forEach(input => input.addEventListener('input', () => {
    state.q = input.value;
    qInputs.forEach(other => other !== input && (other.value = input.value));
    update();
  }));
  // Enter closes the phone keyboard; on desktop focus stays in the box
  qInputs.forEach(input => input.addEventListener('keydown', e => {
    if (e.key === 'Enter') { e.preventDefault(); if (phone.matches) input.blur(); }
  }));
  sorts.forEach(sel => sel.addEventListener('change', () => {
    state.sort = sel.value;
    sorts.forEach(other => (other.value = sel.value));
    update();
  }));
  root.addEventListener('change', e => {
    const box = e.target;
    if (box.matches('[data-asa-type]')) {
      toggleType(box.dataset.asaType, box.checked);
      if (box.checked && CATS.some(c => c.id === box.dataset.asaType)) expand(box.closest('[data-asa-node]'), true);
    } else if (box.matches('[data-asa-surface]')) {
      box.checked ? state.surface.add(box.dataset.asaSurface) : state.surface.delete(box.dataset.asaSurface);
    } else if (box.matches('[data-asa-price]')) {
      box.checked ? state.price.add(box.dataset.asaPrice) : state.price.delete(box.dataset.asaPrice);
    } else return;
    update();
  });
  function clearAll() {
    state.q = '';
    qInputs.forEach(i => (i.value = ''));
    state.type.clear(); state.surface.clear(); state.price.clear();
    update();
  }
  // The chip row is rebuilt on every change: a keyboard user lands on the next chip, or on Filter
  function refocus(i) {
    const left = $$('[data-asa-chips] [data-asa-chip]');
    (left[i] || left[left.length - 1] || opener).focus({ preventScroll: true });
  }
  root.addEventListener('click', e => {
    const ref = e.target.closest('[data-asa-ref]');
    if (ref) {
      const id = ref.dataset.asaRef;
      const hadFocus = ref === document.activeElement;
      tapRefine(id);
      if (hadFocus) $(`[data-asa-ref="${id}"]`)?.focus({ preventScroll: true });
      return;
    }
    const cardEl = e.target.closest('[data-asa-card]');
    const card = cardEl && CARDS.find(c => c.el === cardEl);
    if (card) {
      e.preventDefault();
      if (cardEl.getAttribute('aria-disabled') !== 'true') tapCard(card);
      return;
    }
    const tw = e.target.closest('button.asa-twist');
    if (tw) return expand(tw.closest('[data-asa-node]'), tw.getAttribute('aria-expanded') !== 'true');
    const clear = e.target.closest('[data-asa-clear]');
    if (clear) {
      const hadFocus = clear === document.activeElement && !clear.closest('[data-asa-side]');
      clearAll();
      if (hadFocus) refocus(0);
      return;
    }
    const chip = e.target.closest('[data-asa-chip]');
    if (chip) {
      const { asaChip: k, v } = chip.dataset;
      const at = chip === document.activeElement ? $$('[data-asa-chips] [data-asa-chip]').indexOf(chip) : -1;
      if (k === 'q') { state.q = ''; qInputs.forEach(i => (i.value = '')); }
      if (k === 'type') state.type.delete(v);
      if (k === 'surface') state.surface.delete(v);
      if (k === 'price') state.price.delete(v);
      update();
      if (at >= 0) refocus(at);
      return;
    }
    if (e.target.closest('[data-asa-load-more]')) { visibleLimit += batchSize; update(false); return; }
    if (e.target.closest('[data-asa-open]')) return openSheet();
    if (e.target.closest('[data-asa-close]')) return closeSheet();
  });

  // ---------- filter panel (every width) ----------
  const side = $('[data-asa-side]');
  const opener = $('[data-asa-open]');
  const phone = matchMedia('(max-width: 1023.98px)');
  let lastFocus = null;
  function openSheet() {
    lastFocus = document.activeElement;
    root.classList.add('asa--sheet');
    document.documentElement.classList.add('asa-lock');
    side.setAttribute('role', 'dialog');
    side.setAttribute('aria-modal', 'true');
    opener.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => $('.asa-x', side).focus());
  }
  function closeSheet() {
    if (!root.classList.contains('asa--sheet')) return;
    root.classList.remove('asa--sheet');
    document.documentElement.classList.remove('asa-lock');
    side.removeAttribute('role');
    side.removeAttribute('aria-modal');
    opener.setAttribute('aria-expanded', 'false');
    (lastFocus || opener).focus({ preventScroll: true });
  }
  // The cards are links acting as buttons: Space presses them too
  CARDS.forEach(c => c.el.addEventListener('keydown', e => {
    if (e.key === ' ') { e.preventDefault(); c.el.click(); }
  }));
  document.addEventListener('keydown', e => {
    if (!root.classList.contains('asa--sheet')) return;
    if (e.key === 'Escape') return closeSheet();
    if (e.key !== 'Tab') return;
    const f = $$('button:not([disabled]), input:not([disabled]), select, a[href]', side).filter(el => el.offsetParent);
    if (!f.length) return;
    const [first, last] = [f[0], f[f.length - 1]];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // ---------- end cards ----------
  // Like the homepage: with a mouse, hovering a card's photo turns it into its end card, what's in a kit or a product's
  // benefits card (snippets/ammo-shop-all-item.liquid). No video. Its images load the first time it shows
  const hoverable = matchMedia('(hover: hover) and (pointer: fine)');
  const loadImgs = el => $$('img[data-src]', el).forEach(img => {
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    img.loading = 'eager';
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
  });
  function endCard(li, on) {
    if (on) loadImgs($('[data-asa-end]', li));
    li.classList.toggle('is-end', on);
  }

  // ---------- touch popup ----------
  // Gary, Oct 28: on touch screens a tap on the photo opens the card full screen (a box on tablets): its end card, or
  // its second photo when it has none, with the price and + ADD. Tapping the picture again, or See full details, opens
  // the product. + ADD presses the card's own button, so the cart drawer and the options popup work as everywhere
  // else. The popup has its own history entry: Back closes it, and Back from the product page brings it back
  const pop = $('[data-asa-pop]');
  const popBox = $('.asa-pop__box', pop);
  const popMedia = $('[data-asa-pop-media]', pop);
  const popAdd = $('[data-asa-pop-add]', pop);
  const popChips = $('[data-asa-pop-chips]', pop);
  const popAbout = $('[data-asa-pop-about]', pop);
  const popPoints = $('[data-asa-pop-points]', pop);
  const POP_SIZES = '(min-width: 600px) 480px, 100vw';
  let popItem = null;
  let popFrom = null;
  // A few lines from the product page under the picture, fetched when the popup first opens. AMMO descriptions open
  // with a role line, a headline and a short line, then the first paragraph: the headline and that paragraph
  const ABOUT = new Map();
  function about(h) {
    if (!ABOUT.has(h)) {
      const base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      ABOUT.set(h, fetch(`${base}products/${h}.js`).then(r => (r.ok ? r.json() : null)).then(p => {
        const doc = new DOMParser().parseFromString((p && p.description) || '', 'text/html');
        const clean = el => el.textContent.replace(/\s+/g, ' ').trim();
        const lines = [...doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li')].map(clean).filter(Boolean);
        const i = lines.findIndex(l => l.length >= 80);
        return { head: i > 1 && lines[1].length < 90 ? lines[1] : '', text: i < 0 ? '' : lines[i], points: [...doc.body.querySelectorAll('li')].map(clean).filter(Boolean) };
      }).catch(() => (ABOUT.delete(h), null)));
    }
    return ABOUT.get(h);
  }
  function openPop(it, push = true) {
    const card = it.el;
    const end = $('[data-asa-end]', card);
    const pic = end ? end.cloneNode(true) : ($('.product-card__image--hover img', card) || $('.product-card__image--featured img', card))?.cloneNode(true);
    popMedia.textContent = '';
    if (pic) {
      pic.removeAttribute('aria-hidden');
      pic.removeAttribute('data-asa-end');
      loadImgs(pic);
      // The photo or benefits card fills the popup: ask for a bigger file (the kit panel's photo is only a backdrop)
      (pic.tagName === 'IMG' ? [pic] : pic.matches('.asa-end--card') ? $$('img', pic) : []).forEach(img => {
        img.sizes = POP_SIZES;
        img.loading = 'eager';
      });
      popMedia.append(pic);
    }
    popChips.textContent = '';
    [$('.asa-badges', card), $('.product-card__badge', card)].forEach(b => b && popChips.append(b.cloneNode(true)));
    popAbout.hidden = true;
    popAbout.textContent = '';
    popPoints.textContent = '';
    about(it.h).then(a => {
      if (!a || popItem !== it) return;
      if (a.text) {
        popAbout.innerHTML = `${a.head ? `<p class="asa-pop__lede">${esc(a.head)}</p>` : ''}<p class="asa-pop__text">${esc(a.text)}</p>`;
        popAbout.hidden = false;
      }
      // Oct 31 (Dan's Codex): a benefits card's words are in the picture, so screen readers get the product page's
      // bullet points instead. A card with alt text (snippets/ammo-shop-all-benefits-alt.liquid) already has its words
      const cardImg = pic && pic.matches('.asa-end--card') ? $('img', pic) : null;
      if (cardImg && !cardImg.alt && a.points.length) {
        popPoints.innerHTML = `<ul aria-label="Benefits">${a.points.map(l => `<li>${esc(l)}</li>`).join('')}</ul>`;
      }
    });
    const link = $('.product-card__title', card);
    const tag = $('.product-card__tagline', card);
    $('[data-asa-pop-title]', pop).textContent = link.textContent.trim();
    $('[data-asa-pop-tag]', pop).textContent = tag ? tag.textContent.trim() : '';
    $$('[data-asa-pop-link]', pop).forEach(a => (a.href = link.href));
    popMedia.dataset.href = link.href;
    $('[data-asa-pop-price]', pop).innerHTML = $('.product-card__price-wrapper', card).innerHTML;
    const btn = $('.product-card__atc, .product-card__quick-shop', card);
    popAdd.disabled = !it.avail || !btn;
    popAdd.innerHTML = popAdd.disabled ? 'Sold out' : btn.innerHTML;
    $('.asa-pop__scroll', pop).scrollTop = 0;
    if (!popItem) popFrom = document.activeElement;
    popItem = it;
    pop.hidden = false;
    void pop.offsetWidth;
    pop.classList.add('is-open');
    document.documentElement.classList.add('asa-lock');
    if (push) history.pushState({ ...(history.state || {}), asaPop: it.h }, '');
    $('[data-asa-pop-close]', pop).focus({ preventScroll: true });
  }
  function closePop(fromHistory = false) {
    if (!popItem) return;
    popItem = null;
    pop.classList.remove('is-open');
    pop.hidden = true;
    popMedia.textContent = '';
    if (!root.classList.contains('asa--sheet')) document.documentElement.classList.remove('asa-lock');
    if (popFrom && popFrom.isConnected) popFrom.focus({ preventScroll: true });
    if (!fromHistory && history.state && history.state.asaPop) history.back();
  }
  const popFor = h => items.find(it => it.h === h && !it.el.hidden);
  addEventListener('popstate', () => {
    const h = history.state && history.state.asaPop;
    if (!h) return closePop(true);
    const it = !hoverable.matches && popFor(h);
    if (it) openPop(it, false);
  });
  pop.addEventListener('click', e => {
    if (e.target === pop || e.target.closest('[data-asa-pop-close]')) return closePop();
    if (e.target.closest('[data-asa-pop-media]') && popMedia.dataset.href) {
      flushURL();
      location.assign(popMedia.dataset.href);
    }
  });
  popAdd.addEventListener('click', () => {
    const btn = popItem && $('.product-card__atc, .product-card__quick-shop', popItem.el);
    if (!btn) return;
    closePop();
    btn.click();
  });
  document.addEventListener('keydown', e => {
    if (!popItem) return;
    if (e.key === 'Escape') return closePop();
    if (e.key !== 'Tab') return;
    const f = $$('button:not([disabled]), a[href]', popBox).filter(el => el.offsetParent);
    if (!f.length) return;
    const [first, last] = [f[0], f[f.length - 1]];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // Larry, Oct 29: the turn was slow. A kit's end card fetched its dozen pictures only on the first hover. With a
  // mouse they now load once the card is on screen, so the first hover is instant (not with Save-Data on)
  if (hoverable.matches && 'IntersectionObserver' in window && !(navigator.connection && navigator.connection.saveData)) {
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (!e.isIntersecting) return;
      io.unobserve(e.target);
      loadImgs($('[data-asa-end]', e.target));
    }));
    items.forEach(it => $('[data-asa-end]', it.el) && io.observe(it.el));
  }

  items.forEach(it => {
    const photo = $('.product-card__image-container', it.el);
    if (!photo) return;
    if ($('[data-asa-end]', it.el)) {
      photo.addEventListener('mouseenter', () => hoverable.matches && endCard(it.el, true));
      photo.addEventListener('mouseleave', () => hoverable.matches && endCard(it.el, false));
    }
    photo.addEventListener('click', e => {
      if (hoverable.matches) return;
      e.preventDefault();
      openPop(it);
    });
  });

  // ---------- start ----------
  // The New chip ends with 2026 even if a cached page outlives the date
  $$('[data-asa-new-until]').forEach(el => (el.hidden = Date.now() / 1000 >= +el.dataset.asaNewUntil));
  readURL();
  qInputs.forEach(i => (i.value = state.q));
  sorts.forEach(s => (s.value = state.sort));
  CATS.forEach(c => (state.type.has(c.id) || c.subs.some(s => state.type.has(s.id))) && expand($(`[data-asa-node="${c.id}"]`), true));
  update(false);
  if (more && 'IntersectionObserver' in window) {
    const moreObserver = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && !more.hidden) {
        visibleLimit += batchSize;
        update(false);
      }
    }, { rootMargin: '500px' });
    moreObserver.observe(more);
  }
  // Back from a product opened in the popup, after the browser reloaded the page: open that popup again
  const popAt = !hoverable.matches && history.state && history.state.asaPop && popFor(history.state.asaPop);
  if (popAt) openPop(popAt, false);
  // Coming back to a lit card that sits off to the right of the phone row: slide the row to it
  const row = $('[data-asa-cards]');
  const lit = CARDS.find(cardOn);
  if (row && lit && row.scrollWidth > row.clientWidth) {
    const li = lit.el.parentElement;
    row.scrollLeft = li.offsetLeft - (row.clientWidth - li.offsetWidth) / 2;
  }
  root.classList.add('asa--ready');
})();
